<template>
  <Loader :show="loadingFilters" v-if="loadingFilters"> </Loader>
  <div v-else>
    <MDBRow>
      <MDBCol col="5" offsetLg="7">
        <div class="d-flex flex-column justify-content-end">
          <div>
            <input v-model="searchText" type="text" class="form-control" :placeholder="$t('brochures.searchMedia')"
              :disabled="isInteractionLocked" />
          </div>
          <span class="d-flex justify-content-end">
            <MDBBtn color="primary" @click="scheduleFilter({ resetPage: true })" :disabled="isInteractionLocked">{{
              $t("global.buttons.toSearch")
              }}</MDBBtn>
          </span>
        </div>
      </MDBCol>
    </MDBRow>

    <MDBRow class="pb-3 pt-3">
      <MDBCol col="12" v-html="$t('global.buttons.setFilters')">
      </MDBCol>
    </MDBRow>

    <MDBRow v-if="categories.length > 0">
      <MDBCol cols="12" lg="6" xl="3" class="mb-4" v-for="(data, i) in categories" :key="i">
        <div class="d-flex justify-content-start align-items-center pb-3">
          <span class="fw-bold">{{
            $t("brochures.brochureCategories." + data.name)
            }}</span>
        </div>

        <div>
          <div class="d-flex justify-content-start align-items-center" v-for="child in data.children" :key="child.id">
            <MDBSwitch @input="filterFiles(child.id, child.parent_id)" v-model="categorySelections[child.id]"
              :disabled="isInteractionLocked" />
            <span>{{ $t("brochures.brochureCategories." + child.name) }}</span>
          </div>
        </div>
      </MDBCol>
      <MDBCol cols="12" class="d-flex justify-content-end"><span @click="clearFilters()"
          class="text-decoration-underline cursor--pointer" :class="{ 'opacity-50': isInteractionLocked }">{{
            $t("global.buttons.clearFilters") }}
        </span></MDBCol>
    </MDBRow>

    <Loader :show="showResultsLoader" v-if="showResultsLoader"></Loader>
    <div v-else class="pt-5">
      <MDBRow class="pb-3">
        <MDBCol cols="12" lg="6"><span class="fw-bold">{{ $t("brochures.searchResults") }}:</span>
          {{ totalMediaFiles }}</MDBCol>
        <MDBCol cols="12" lg="6" class="d-flex justify-content-end"><span
            class="me-2 cursor--pointer text-decoration-underline" @click="selectAll()">{{
              $t("global.buttons.selectAll")
            }}</span>
          <span @click="deselectAll()" class="cursor--pointer text-decoration-underline">{{
            $t("global.buttons.deselectAll")
            }}</span>
        </MDBCol>
      </MDBRow>
      <MDBRow class="pb-3">
        <MDBCol cols="12" class="d-flex justify-content-end">
          <MDBBtn type="button" color="danger" @click="downloadAll()" :disabled="!selectedItemIds.length">{{
            $t("global.buttons.downloadSelection") }}</MDBBtn>
        </MDBCol>
      </MDBRow>
      <MDBRow class="pb-5">
        <MDBCol cols="12" lg="6">{{ $t("global.sortBy.choiceBy") }}:</MDBCol>
        <MDBCol cols="12" lg="6" class="d-flex justify-content-end"><span v-if="showBrochureDateSort">{{ $t("global.sortBy.date") }}
            <img @click="sortData('created_at', 'desc')" :src="require('@/assets/img/icons/arrow_up_black.png')"
              alt="filter_btn" class="cursor--pointer" />
            <img @click="sortData('created_at', 'asc')" :src="require('@/assets/img/icons/arrow_down_black.png')"
              alt="filter_btn" class="cursor--pointer" /></span>
          <span>{{ $t("global.sortBy.name") }}
            <img @click="sortData('name', 'desc')" :src="require('@/assets/img/icons/arrow_up_black.png')"
              alt="filter_btn" class="cursor--pointer" />
            <img @click="sortData('name', 'asc')" :src="require('@/assets/img/icons/arrow_down_black.png')"
              alt="filter_btn" class="cursor--pointer" /></span>
        </MDBCol>
      </MDBRow>
      <MDBRow v-if="mediaFiles && mediaFiles.length > 0">
        <MDBCol cols="12" md="6" xl="4" class="mb-4" v-for="media in mediaFiles" :key="media.id">
          <DocumentPreview
            :file="media"
            :selected="isItemSelected(media.id)"
            @toggle="selectItems"
          >
            <template #actions>
              <span
                @click.stop="dowloadSingleFile(media.id)"
                class="text-decoration-underline cursor--pointer"
              >Download</span>
              |
              <a :href="media.file" target="_blank" rel="noopener noreferrer" class="text-danger text-decoration-underline cursor--pointer">{{
                $t("global.buttons.preview") }}</a>
            </template>
          </DocumentPreview>
        </MDBCol>
      </MDBRow>
      <MDBRow v-if="paginationLinks && mediaFiles && mediaFiles.length > 0">
        <MDBCol cols="12" class="d-flex justify-content-center">
          <MDBPagination>
            <MDBPageItem v-for="(paginationLink, index) in localizedPaginationLinks" :key="index"><span class="cursor--pointer"
                :class="`${!paginationLink?.active ? '' : 'text-decoration-underline'
                  }`" v-html="paginationLink?.label" @click="paginate(paginationLink?.url)"></span></MDBPageItem>
          </MDBPagination>
        </MDBCol>
      </MDBRow>
    </div>
  </div>
</template>

<script setup>
import DocumentPreview from "@/components/DocumentPreview/DocumentPreview.vue";
import Loader from "@/components/General/Loader/index.vue";
import {
  MDBRow,
  MDBCol,
  MDBSwitch,
  MDBBtn,
  MDBPagination,
  MDBPageItem,
} from "mdb-vue-ui-kit";
import { onMounted, onBeforeUnmount, computed, ref } from "vue";
import { PriceService } from "@/services/price.service";
import { useItemsConditions } from '@/composables/useItemsConditions'
import { useRouter } from "vue-router";
import { useI18n } from "vue-i18n";
import { useTenantConfig } from "@/composables/useTenantConfig";

import { useStore } from "vuex";

const router = useRouter();
const { t } = useI18n();
const { tenantConfig } = useTenantConfig();
const { getContext } = useItemsConditions()
const context = computed(() => getContext())

const store = useStore();

const categories = computed(() => store.getters.getBrochureCategories);
const loading = computed(() => store.getters.getPending);
const mediaFiles = computed(() => store.getters.getFileBrochures);
const paginationLinks = computed(() => store.getters.getPaginationLinks);
const localizedPaginationLinks = computed(() =>
  (paginationLinks.value || []).map((paginationLink) => ({
    ...paginationLink,
    label: getLocalizedPaginationLabel(paginationLink?.label),
  }))
);
const totalMediaFiles = computed(() => store.getters.getTotalMediaFiles);

const selectedItemIds = ref([]);
const selectedCategories = ref([]);
const categorySelections = ref([]);
const loadingFilters = ref(true);
const searchText = ref(null);
const page = ref(1);
const sortField = ref("created_at");
const sortType = ref("desc");
const filterDebounceTimeout = ref(null);
const isFiltering = ref(false);
const isInteractionLocked = computed(() => loadingFilters.value || isFiltering.value);
const showResultsLoader = computed(() => loading.value || isFiltering.value);
const showBrochureDateSort = computed(
  () => tenantConfig.value?.showBrochureDateSort ?? true
);

const filterFiles = (id, parentId) => {
  if (isInteractionLocked.value) {
    return;
  }

  // If the ID already exists, remove it
  const data = `${id}_${parentId}`;
  const existingIndex = selectedCategories.value.indexOf(data);
  if (existingIndex !== -1) {
    selectedCategories.value.splice(existingIndex, 1);
  } else {
    selectedCategories.value.push(data);
  }

  scheduleFilter({ resetPage: true });
};

const sortData = (field, type) => {
  if (isInteractionLocked.value) {
    return;
  }

  sortField.value = field;
  sortType.value = type;

  scheduleFilter({ resetPage: true });
};

const filter = async () => {
  if (isFiltering.value) {
    return;
  }

  try {
    deselectAll();
    const data = {
      filters: selectedCategories.value,
      searchText: searchText.value,
      page: page.value,
      sortField: sortField.value,
      sortType: sortType.value,
      itemsPerPage: 9,
    };
    isFiltering.value = true;
    await store.dispatch("filterBrochureFiles", data);
  } finally {
    isFiltering.value = false;
  }
};

const scheduleFilter = ({ resetPage = false } = {}) => {
  if (resetPage) {
    page.value = 1;
  }

  if (filterDebounceTimeout.value) {
    clearTimeout(filterDebounceTimeout.value);
  }

  filterDebounceTimeout.value = setTimeout(() => {
    filter();
  }, 1000);
};

const paginate = async (label) => {
  if (isInteractionLocked.value || label === null) {
    return;
  }

  if (filterDebounceTimeout.value) {
    clearTimeout(filterDebounceTimeout.value);
  }

  page.value = label.match(/\d*$/g)[0];

  await filter();
};

const getLocalizedPaginationLabel = (label) => {
  if (typeof label !== "string") {
    return label;
  }

  const lowerLabel = label.toLowerCase();

  if (lowerLabel.includes("previous")) {
    return `&laquo; ${t("global.buttons.back")}`;
  }

  if (lowerLabel.includes("next")) {
    return `${t("global.buttons.next")} &raquo;`;
  }

  return label;
};

const selectItems = (media) => {
  const existingIndex = selectedItemIds.value.indexOf(media.id);
  if (existingIndex !== -1) {
    selectedItemIds.value.splice(existingIndex, 1);
  } else {
    selectedItemIds.value.push(media.id);
  }
};

const isItemSelected = (id) => selectedItemIds.value.includes(id);

const downloadFile = async () => {
  const response = await PriceService.downloadBrochureFiles(selectedItemIds.value);

  window.open(response.data);
};

const selectAll = () => {
  deselectAll();
  for (let media of mediaFiles.value) {
    selectItems(media);
  }
};

const downloadAll = () => {
  downloadFile(selectedItemIds.value);
};

const dowloadSingleFile = (id) => {
  selectedItemIds.value = [];
  selectedItemIds.value.push(id);
  downloadFile(selectedItemIds.value);
};

const deselectAll = () => {
  selectedItemIds.value = [];
};

onMounted(async () => {
  if (context.value.canSeePriceBrochures) {
    await clearFilters();

    loadingFilters.value = false;
  } else {
    router.push({
      name: "404",
    });
  }
});

onBeforeUnmount(() => {
  if (filterDebounceTimeout.value) {
    clearTimeout(filterDebounceTimeout.value);
  }
});

const clearFilters = async () => {
  if (isFiltering.value) {
    return;
  }

  loadingFilters.value = true;
  if (filterDebounceTimeout.value) {
    clearTimeout(filterDebounceTimeout.value);
  }
  selectedItemIds.value = [];
  selectedCategories.value = [];
  page.value = 1;
  searchText.value = null;

  await store.dispatch("fetchBrochureCategories");

  for (let category of categories.value) {
    if (category.children.length > 0) {
      for (let child of category.children) {
        categorySelections.value[child.id] = false;
      }
    }
  }

  categorySelections.value = categorySelections.value.filter(
    (element) => element !== undefined
  );

  await filter();
  loadingFilters.value = false;
};
</script>

<style scoped lang="scss">
@import "@/styles/_variables.scss";
@import "@/styles/_forms.scss";
</style>
